import { ref, readonly } from "vue";
import * as msal from "@azure/msal-browser";
import { msalConfig, loginRequest } from "@/features/auth/services/authConfig";
import { acquireGraphTokenSilently, AuthError, markTokenValid, markTokenInvalid } from "@/features/auth/services/token-service";
import { setSsoToken, clearSsoToken } from "@/features/profile/services/graph-api";

let msalInstance: msal.PublicClientApplication | null = null;
let routerPush: ((path: string) => void) | null = null;

const isLoggedIn = ref(false);
const isLoading = ref(true);

/**
 * Composable de autenticação que gerencia login, logout e estado do MSAL.
 *
 * @returns Objeto com estado reativo de autenticação e métodos de login/logout.
 *
 * @example
 * ```typescript
 * const { isLoggedIn, init, loginPopup, logout } = useAuth();
 * await init();
 * if (!isLoggedIn.value) await loginPopup();
 * ```
 */
export function useAuth() {

  /**
   * Registra a função de navegação do router para redirecionamento pós-logout.
   *
   * @param fn - Função de push do Vue Router para navegação programática.
   *
   * @example
   * ```typescript
   * const { setRouterPush } = useAuth();
   * setRouterPush((path) => router.push(path));
   * ```
   */
  function setRouterPush(fn: (path: string) => void): void {
    routerPush = fn;
  }

  /**
   * Verifica se há dados de usuário salvos no roaming settings do Office ou localStorage.
   *
   * @returns true ou false indicando se há dados de usuário armazenados.
   *
   * @example
   * ```typescript
   * if (hasStoredUserData()) {
   *   console.log("Dados de usuário encontrados.");
   * }
   * ```
   */
  function hasStoredUserData(): boolean {
    try {
      if (typeof Office !== "undefined" && Office.context?.roamingSettings) {
        const roaming = Office.context.roamingSettings.get("helpitech_user_info");
        if (roaming && roaming !== "" && roaming !== "null") return true;
      }

      const local = localStorage.getItem("user_info");
      return !!local && local !== "null";
    } catch {
      return false;
    }
  }

  /**
   * Retorna a instância MSAL inicializada.
   * Cria uma nova instância se necessário (compatível com SSO mobile).
   *
   * @returns A instância ativa do {@link msal.PublicClientApplication}.
   *
   * @example
   * ```typescript
   * const instance = getMsalInstance();
   * const accounts = instance.getAllAccounts();
   * ```
   */
  function getMsalInstance(): msal.PublicClientApplication {
    if (!msalInstance) {
      msalInstance = new msal.PublicClientApplication(msalConfig);
    }
    return msalInstance;
  }

  /**
   * Inicializa a instância MSAL necessária para Graph API
   *
   * @returns Promise que resolve quando a inicialização é concluída.
   *
   * @example
   * ```typescript
   * const { init } = useAuth();
   * await init();
   * ```
   */
  async function init(): Promise<void> {
    if (!msalInstance) {
      msalInstance = new msal.PublicClientApplication(msalConfig);
      await msalInstance.initialize();
    }

    try {
      try {
        const redirectResponse = await msalInstance.handleRedirectPromise();
        if (redirectResponse) {
          isLoggedIn.value = true;
          markTokenValid();
        }
      } catch (err: any) {
        console.error("[useAuth] handleRedirectPromise erro:", err?.errorCode || err?.message);
      }

      if (isLoggedIn.value) return;

      const accounts = msalInstance.getAllAccounts();
      isLoggedIn.value = accounts.length > 0;

      if (isLoggedIn.value && accounts[0]) {
        try {
          await acquireGraphTokenSilently(msalInstance, accounts[0]);
        } catch (err: any) {
          if (err instanceof AuthError && err.interactionRequired) {
            isLoggedIn.value = false;
          } else {
            console.error("[useAuth] Falha ao renovar:", err?.originalError?.errorCode || err?.message);
          }
        }
      }

      if (!isLoggedIn.value && hasStoredUserData()) {
        markTokenInvalid();
        isLoggedIn.value = true;
      }
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Realiza login via redirect (full-page navigation).
   *
   * Indicado para mobile, onde o webview do Outlook bloqueia popups.
   * Redireciona a página atual para o login da Microsoft e,
   * após autenticação, retorna para o add-in.
   *
   * ATENÇÃO: Esta função não resolve a promise — a página será redirecionada.
   * O resultado do login é processado por {@link init} via `handleRedirectPromise`.
   *
   * @example
   * ```typescript
   * const { loginRedirect } = useAuth();
   * loginRedirect(); // página será redirecionada
   * ```
   */
  function loginRedirect(): void {
    const instance = getMsalInstance();
    instance.loginRedirect({
      ...loginRequest,
      redirectUri: window.location.origin + "/index.html",
    });
  }

  /**
   * Realiza o logout do usuário, usando `logoutPopup` (compatível com iframes do
   * Outlook Web/New) e fallback para `logoutRedirect`. Após logout, redireciona
   * para a página de login.
   *
   * @returns Promise que resolve quando o logout é concluído.
   *
   * @example
   * ```typescript
   * const { logout } = useAuth();
   * await logout();
   * ```
   */
  async function logout(): Promise<void> {
    markTokenInvalid();
    clearSsoToken();
    const instance = getMsalInstance();
    const account = instance.getAllAccounts()[0];

    if (account) {
      try {
        await instance.logoutPopup({
          account,
          postLogoutRedirectUri: window.location.origin + "/index.html",
        });
      } catch (popupErr: any) {
        console.error("[useAuth] logoutPopup falhou:", popupErr?.errorCode || popupErr?.message);
        
        try {
          instance.logoutRedirect({
            account,
            postLogoutRedirectUri: window.location.origin + "/index.html",
          });
        } catch (redirectErr: any) {
          console.error("[useAuth] logoutRedirect também falhou:", redirectErr?.errorCode || redirectErr?.message);
        }
      }
    }

    isLoggedIn.value = false;
    if (routerPush) {
      routerPush("/login");
    } else {
      window.location.href = "/index.html";
    }
  }

  /**
   * Login via SSO do Office (Outlook mobile e desktop).
   *
   * Usa `Office.auth.getAccessToken()` para obter um token sem popup —
   * essencial no mobile, onde o webview bloqueia popups do MSAL.
   *
   * @returns Promise que resolve quando o login SSO é concluído.
   * @throws Se `Office.auth` não estiver disponível ou o token for negado.
   *
   * @example
   * ```typescript
   * const { loginWithSSO } = useAuth();
   * await loginWithSSO();
   * ```
   */
  async function loginWithSSO(): Promise<void> {
    if (!Office.auth?.getAccessToken) {
      throw new Error("Office.auth.getAccessToken não disponível.");
    }

    const token = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: true,
    });

    setSsoToken(token);
    isLoggedIn.value = true;
    markTokenValid();
  }


  /**
   * Realiza login via popup (indicado para desktop: Classic, New e OWA).
   *
   * Se o popup for bloqueado pelo Outlook desktop, o erro é propagado
   * para que o chamador possa fazer fallback para redirect.
   *
   * @returns Promise que resolve quando o login é concluído.
   * @throws BrowserAuthError se o popup for bloqueado.
   *
   * @example
   * ```typescript
   * try {
   *   await loginPopup();
   * } catch {
   *   loginRedirect(); // fallback
   * }
   * ```
   */
  async function loginPopup(): Promise<void> {
    const instance = getMsalInstance();
    await instance.initialize();
    const result = await instance.loginPopup({
      ...loginRequest,
      redirectUri: window.location.origin + "/index.html",
    });
    if (result) {
      isLoggedIn.value = true;
      markTokenValid();
    }
  }

  return {
    isLoggedIn: readonly(isLoggedIn),
    isLoading: readonly(isLoading),
    init,
    getMsalInstance,
    loginRedirect,
    loginWithSSO,
    logout,
    setRouterPush,
    loginPopup,
  };
}

